import { useEffect, useState } from 'react';
import { supabase } from '../../supabase';

export default function Reports() {
  console.log('[Reports.jsx] mounted');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [filter, setFilter] = useState('open'); // open | handled | all

  const load = async () => {
    setLoading(true);
    let q = supabase.from('reports').select('*').order('created_at', { ascending: false });
    if (filter === 'open') q = q.eq('status', 'open');
    else if (filter === 'handled') q = q.neq('status', 'open');
    const { data: rs } = await q;

    if (!rs || rs.length === 0) { setReports([]); setLoading(false); return; }

    // Hydrate reporter + reported profiles
    const ids = [...new Set([...rs.map(r => r.reporter_id), ...rs.map(r => r.reported_id)])];
    const { data: profs } = await supabase.from('profiles').select('id, name, phone, banned').in('id', ids);
    const map = Object.fromEntries((profs || []).map(p => [p.id, p]));

    setReports(rs.map(r => ({ ...r, reporter: map[r.reporter_id], reported: map[r.reported_id] })));
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const adminPhone = sessionStorage.getItem('admin_phone');

  const handle = async (action) => {
    if (!selected) return;
    const update = {
      status: 'handled',
      handled_at: new Date().toISOString(),
      handled_by: adminPhone,
      action_taken: action,
      admin_note: note || null,
    };
    await supabase.from('reports').update(update).eq('id', selected.id);

    if (action === 'warn') {
      await supabase.from('warnings').insert({
        user_id: selected.reported_id,
        reason: note || selected.reason,
        issued_by: adminPhone,
      });
    } else if (action === 'ban') {
      await supabase.from('profiles').update({
        banned: true,
        banned_at: new Date().toISOString(),
        banned_reason: note || selected.reason,
        banned_by: adminPhone,
      }).eq('id', selected.reported_id);
    }

    setSelected(null);
    setNote('');
    load();
  };

  return (
    <div>
      <div style={S.headerRow}>
        <h1 style={S.h1}>Reports</h1>
        <div style={S.filters}>
          {['open', 'handled', 'all'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{...S.filterBtn, ...(filter === f ? S.filterBtnActive : {})}}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? <p style={S.dim}>Loading…</p> :
        reports.length === 0 ? <p style={S.dim}>No reports in this view.</p> :
        <table style={S.table}>
          <thead>
            <tr style={S.tr}>
              <th style={S.th}>When</th>
              <th style={S.th}>Reporter</th>
              <th style={S.th}>Reported</th>
              <th style={S.th}>Reason</th>
              <th style={S.th}>Status</th>
              <th style={S.th}></th>
            </tr>
          </thead>
          <tbody>
            {reports.map(r => (
              <tr key={r.id} style={S.tr}>
                <td style={S.td}>{new Date(r.created_at).toLocaleDateString()}</td>
                <td style={S.td}>{r.reporter?.name || r.reporter_id.slice(0, 8)}</td>
                <td style={S.td}>
                  {r.reported?.name || r.reported_id.slice(0, 8)}
                  {r.reported?.banned && <span style={S.banned}> BANNED</span>}
                </td>
                <td style={{...S.td, color: '#fff'}}>{r.reason}</td>
                <td style={S.td}>
                  <span style={r.status === 'open' ? S.statusOpen : S.statusHandled}>
                    {r.status === 'open' ? 'Open' : r.action_taken || 'Handled'}
                  </span>
                </td>
                <td style={S.td}>
                  <button style={S.viewBtn} onClick={() => { setSelected(r); setNote(r.admin_note || ''); }}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      }

      {selected && (
        <div style={S.modalBg} onClick={() => setSelected(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <h2 style={S.h2}>Report details</h2>
            <p style={S.dim}>Filed {new Date(selected.created_at).toLocaleString()}</p>

            <div style={S.section}>
              <div style={S.sectionLabel}>Reason</div>
              <div style={S.sectionVal}>{selected.reason}</div>
            </div>
            <div style={S.section}>
              <div style={S.sectionLabel}>Reporter</div>
              <div style={S.sectionVal}>{selected.reporter?.name || 'Unknown'} · {selected.reporter?.phone}</div>
            </div>
            <div style={S.section}>
              <div style={S.sectionLabel}>Reported user</div>
              <div style={S.sectionVal}>
                {selected.reported?.name || 'Unknown'} · {selected.reported?.phone}
                {selected.reported?.banned && <span style={S.banned}> · BANNED</span>}
              </div>
            </div>

            <textarea
              style={S.textarea}
              placeholder="Admin note (optional)"
              value={note}
              onChange={e => setNote(e.target.value)}
            />

            {selected.status === 'open' ? (
              <div style={S.actions}>
                <button style={{...S.btn, ...S.btnDismiss}} onClick={() => handle('dismissed')}>Dismiss</button>
                <button style={{...S.btn, ...S.btnWarn}} onClick={() => handle('warn')}>Warn user</button>
                <button style={{...S.btn, ...S.btnBan}} onClick={() => handle('ban')}>Ban user</button>
              </div>
            ) : (
              <p style={S.dim}>Already handled: <strong>{selected.action_taken}</strong> by {selected.handled_by}</p>
            )}
            <button style={S.cancel} onClick={() => setSelected(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  h1: { fontSize: 28, fontWeight: 800, margin: 0 },
  h2: { fontSize: 22, fontWeight: 800, margin: '0 0 4px 0' },
  dim: { color: '#888', fontSize: 13, margin: '4px 0' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  filters: { display: 'flex', gap: 8 },
  filterBtn: { padding: '6px 14px', borderRadius: 999, background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#888', cursor: 'pointer', fontSize: 13 },
  filterBtnActive: { background: 'rgba(167,139,250,0.15)', color: '#a78bfa' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tr: { borderBottom: '1px solid rgba(255,255,255,0.06)' },
  th: { textAlign: 'left', padding: '12px 16px', color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 },
  td: { padding: '14px 16px', color: '#aaa', fontSize: 13 },
  statusOpen: { background: 'rgba(251,191,36,0.15)', color: '#fbbf24', padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700 },
  statusHandled: { background: 'rgba(255,255,255,0.05)', color: '#888', padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700 },
  banned: { background: 'rgba(248,113,113,0.15)', color: '#f87171', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 800, marginLeft: 6 },
  viewBtn: { background: 'rgba(167,139,250,0.15)', border: '1px solid rgba(167,139,250,0.3)', color: '#a78bfa', padding: '6px 14px', borderRadius: 999, fontSize: 12, cursor: 'pointer' },
  modalBg: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 },
  modal: { background: '#1a0a2e', borderRadius: 16, maxWidth: 540, width: '100%', maxHeight: '90vh', overflow: 'auto', padding: 28 },
  section: { marginTop: 16 },
  sectionLabel: { fontSize: 10, color: '#888', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 4 },
  sectionVal: { color: '#fff', fontSize: 14 },
  textarea: { width: '100%', boxSizing: 'border-box', minHeight: 70, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 14, padding: 12, marginTop: 16, fontFamily: 'inherit', outline: 'none', resize: 'vertical' },
  actions: { display: 'flex', gap: 8, marginTop: 16 },
  btn: { flex: 1, padding: 12, borderRadius: 999, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer' },
  btnDismiss: { background: 'rgba(255,255,255,0.05)', color: '#aaa', border: '1px solid rgba(255,255,255,0.1)' },
  btnWarn: { background: 'rgba(251,191,36,0.2)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' },
  btnBan: { background: 'rgba(248,113,113,0.2)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' },
  cancel: { width: '100%', padding: 10, marginTop: 8, background: 'none', border: 'none', color: '#888', fontSize: 13, cursor: 'pointer' },
};
